import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { asPipelineId } from '@ludus/core'
import {
  NODE_SPECS,
  isKnownNodeType,
  pipelinesNamedBy,
  type FlowGraph,
  type FlowNode,
  type NodeType,
  type OverviewRow,
} from '@/features/flows/types.ts'
import { fetchPipelineManagers, fetchPipelineStatuses, listPipelines } from '@/lib/api/pipelines.ts'
import type { PipelineId } from '@ludus/core'

/**
 * Turns the ids an overview row carries into the names an operator gave them.
 *
 * This is the job `OverviewRow` was declared for — "the console translates the
 * row and resolves an id to a human name **without learning what any node
 * type's options mean**" (`packages/core/src/flows/nodes.ts`). It keeps that
 * promise by reading the spec instead of the option: a row's `label` *is* the
 * option's name, so the field it came from says whether the text is a pipeline,
 * a status, a manager or a plain value — and `NodeField.scopedBy` says which
 * board's statuses and managers to look it up in. Nothing here branches on
 * `create_lead` or on `targetStatusId`, so a fifth node type resolves for free.
 *
 * **Which boards to load comes from the graph**, through core's
 * `pipelinesNamedBy` — the same reading the runtime uses to decide which boards
 * to pre-fill `$sale.openCardsIn` for. A `create_lead` node points at another
 * pipeline, so the flow's own board is never enough.
 *
 * **One SWR key covers the whole set, rather than a hook per board.** The set is
 * read off the draft and changes as nodes are added, and a hook per board would
 * be a hook count that changes between renders. The key names the boards, so
 * pointing an action at a new pipeline is a new key and a fetch, and pointing it
 * back is a cache hit.
 */

/**
 * A node type as an i18n key stem: `trigger.status_entered` → `trigger_status_entered`.
 *
 * The dot has to go because i18next reads it as a key separator, and a type
 * spelled with one would nest `status_entered` under `trigger` in the locale
 * files — a shape a translator would have to be told about. Derived rather than
 * mapped, so a fifth node type needs a row in the two locale files and nothing
 * else.
 */
export const nodeTypeKey = (type: NodeType): string => type.replaceAll('.', '_')

interface BoardNames {
  statuses: Map<string, string>
  managers: Map<string, string>
}

export interface NodeLabels {
  /** The row's text with ids resolved; the raw text when there is nothing to resolve. */
  text: (node: FlowNode, row: OverviewRow) => string
}

// There is deliberately no `loading` here. It was declared and documented but
// never read, and the two `isLoading` deps it needed gave this object a new
// identity on every load/settle transition — which rebuilt the entire xyflow
// node array for a value nothing rendered, defeating the memo's stated purpose.
// A row whose lookup has not arrived already shows its raw id, which is the
// same thing a loading flag would have been used to say.

export function useNodeLabels(pipelineId: PipelineId, graph: FlowGraph | null): NodeLabels {
  const { t } = useTranslation()
  const boards = useMemo(
    () => (graph === null ? [] : [...pipelinesNamedBy(graph, pipelineId)].sort()),
    [graph, pipelineId],
  )

  const pipelinesSwr = useSWR('/pipelines', listPipelines)

  const boardsSwr = useSWR(
    boards.length > 0 ? `/flows/labels?boards=${boards.join(',')}` : null,
    async (): Promise<Map<string, BoardNames>> => {
      // `allSettled`, not `all`: a `create_lead` node can point at a board that
      // has since been deleted, and one 404 rejecting the whole batch left
      // `data` undefined — so every status and manager on every card fell back
      // to its raw id, including on boards that had answered perfectly well.
      // One stale pointer must cost its own labels and nothing else.
      const settled = await Promise.allSettled(
        boards.map(async (id) => {
          const board = asPipelineId(id)
          const [statuses, managers] = await Promise.all([
            fetchPipelineStatuses(board),
            fetchPipelineManagers(board),
          ])
          return [
            id,
            {
              statuses: new Map(statuses.map((s) => [String(s.id), s.name])),
              managers: new Map(
                managers.map((m) => [String(m.id), `${m.firstName} ${m.lastName}`.trim()]),
              ),
            },
          ] as const
        }),
      )
      return new Map(settled.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : [])))
    },
  )

  const pipelineNames = useMemo(
    () => new Map((pipelinesSwr.data ?? []).map((p) => [String(p.id), p.name])),
    [pipelinesSwr.data],
  )

  // Memoised because the canvas derives every xyflow node from this object: a
  // new identity each render would rebuild the whole node array each render,
  // for a value that only changes when a lookup arrives.
  const boardNames = boardsSwr.data
  return useMemo<NodeLabels>(
    () => ({
      text: (node, row) => {
        // Guarded for the same reason `overviewOf` is: `type` is a plain string
        // at the wire boundary, and indexing the registry with an unknown one
        // threw where the card is rendered.
        if (!isKnownNodeType(node.type)) return row.text
        const field = NODE_SPECS[node.type].fields.find((f) => f.name === row.label)
        if (field === undefined) return row.text
        if (field.type === 'pipeline') return pipelineNames.get(row.text) ?? row.text
        // A closed choice list is the one kind of value that is neither an id
        // nor a person's own words: `cycle: 'same'` is a member of
        // `FLOW_CYCLES`, so it is translated rather than printed. Keyed by
        // field and value, so a new choice is two locale rows and no code.
        if (field.type === 'select') {
          return t(`pages.flows.canvas.choice.${field.name}.${row.text}`, {
            defaultValue: row.text,
          })
        }
        if (field.type !== 'status' && field.type !== 'manager') return row.text

        // `scopedBy` names the option holding the board; absent means the
        // flow's own. Core declares that link so the console does not have to
        // know that `targetStatusId` belongs to `targetPipelineId`.
        const scope =
          field.scopedBy === undefined ? String(pipelineId) : String(node.options[field.scopedBy])
        const board = boardNames?.get(scope)
        if (board === undefined) return row.text
        const names = field.type === 'status' ? board.statuses : board.managers
        return names.get(row.text) ?? row.text
      },
    }),
    [t, pipelineId, pipelineNames, boardNames],
  )
}
